/**
 * Motor de recargos automáticos.
 *
 * Replica la hoja «PRODUCTO» (Tabla3) del Excel, que decide qué partidas de
 * recargo aplican a cada guía a partir del producto, la planta, la cantidad
 * y los tiempos.
 *
 * Corrige el defecto S1: en el Excel la tabla estaba desalineada con GUIAS y
 * funcionaba «por coincidencia posicional» — las filas 2 a 7 daban #VALUE! y
 * las 46 a 205 apuntaban fuera de la tabla.
 *
 * Ver ANALISIS-PLANILLAS.md §3.7 y PROMPT-MAESTRO §5.3.
 */

import type {
  CodigoPlanta,
  ItemContrato,
  ProductoParseado,
  RecargoCalculado,
  TiemposGuia,
  UnidadItem,
} from './tipos';
import { roundTo } from './precios';

/** Cono base del contrato. Toda diferencia respecto de 13 se recarga. */
export const CONO_BASE = 13;

/** Factor de conversión de shotcrete a m³ equivalentes para la diferencia de cono. */
export const FACTOR_CONO_SHOTCRETE = 7;

/** Kilos de fibra por m³ según el extra del código de producto. */
export const KG_FIBRA_POR_M3: Record<string, number> = {
  FP3: 3,
  FP5: 5,
  FP6: 6,
  'FP0,8': 0.8,
};

/** Códigos de partida usados por el motor. En producción vienen de la BD. */
export const ITEMS_RECARGO = {
  CARGA_INCOMPLETA: '4.3',
  SOBREESTADIA: '4.5',
  BOMBEO: '4.6',
  AIRE_25_35: '4.13',
  AIRE_40_50: '4.14',
  DIF_CONO_HORMIGON: '4.16',
  DIF_CONO_SHOTCRETE: '4.17',
  FIBRA: '10.8',
  R3: '10.10',
  R7: '10.11',
  CONFIANZA_95: '10.17',
  IMPERMEABILIZANTE: '15.1',
} as const;

export interface EntradaRecargos {
  plantaCodigo: CodigoPlanta;
  /** Carga mínima facturable de la planta: KM 13 → 5 · NV 17 → 2 · VALLE → 5. */
  minCargaM3: number;
  cantidadM3: number;
  producto: ProductoParseado;
  /** `false` para ARENA, TRASLADO, NULA… No se le aplican recargos. */
  generaRecargos: boolean;
  tiempos: TiemposGuia;
  codigoProductoNormalizado: string;
}

/** Busca una partida por código. Devuelve `null` si el contrato no la tiene. */
type BuscarItem = (codigo: string) => (ItemContrato & { precioUf: number }) | null;

/**
 * Calcula todos los recargos que aplican a una guía.
 * Devuelve una línea por recargo, con su cantidad, precio y monto en UF.
 */
export function calcularRecargos(e: EntradaRecargos, buscarItem: BuscarItem): RecargoCalculado[] {
  const out: RecargoCalculado[] = [];
  const { producto: p, cantidadM3: m3, tiempos } = e;

  const agregar = (
    regla: RecargoCalculado['regla'],
    codigo: string,
    cantidad: number,
    unidad: UnidadItem,
    detalle: string,
  ) => {
    if (cantidad <= 0) return;
    const item = buscarItem(codigo);
    if (!item) return; // el contrato no contempla la partida: no se inventa
    out.push({
      regla,
      itemCodigo: codigo,
      cantidad: roundTo(cantidad, 3),
      unidad,
      precioUf: item.precioUf,
      montoUf: roundTo(cantidad * item.precioUf, 4),
      detalle,
    });
  };

  // ── 1. Sobrestadía ─────────────────────────────────────────────────────
  // Aplica a TODA guía con tiempos completos, sea hormigón o no.
  // Es el recargo que el Excel dejaba de facturar en ~20.000 bloques (riesgo R2).
  if (tiempos.bloquesCodelco !== null && tiempos.bloquesCodelco > 0) {
    agregar(
      'SOBREESTADIA',
      ITEMS_RECARGO.SOBREESTADIA,
      tiempos.bloquesCodelco,
      'UF/M3',
      `Estadía ${tiempos.estadiaObra} min · sobre 45 min libres: ${tiempos.sobreEstadia} min ` +
        `· factor ${tiempos.factor10Min} → ${tiempos.bloquesCodelco} bloques de 10 min`,
    );
  }

  /**
   * La apertura de planta no es un recargo: es una línea propia.
   *
   * Venía como recargo automático apuntado a la partida 4.9, que es
   * «HORMIGON R1 G50» —la 4.4 es la apertura—. Y aunque hubiera apuntado
   * bien, habría cobrado dos veces: la apertura llega como su propia guía,
   * con su partida escrita, en la hoja de productos especiales. Cobraba
   * 27,295 UF de más en cada estado de pago que la tuviera.
   */
  if (e.codigoProductoNormalizado === 'APERTURA DE PLANTA') return out;

  // A partir de aquí, sólo hormigón.
  if (!e.generaRecargos || !p.grado) return out;

  // ── 3. Carga incompleta ────────────────────────────────────────────────
  if (m3 > 0 && m3 < e.minCargaM3) {
    const diferencia = roundTo(e.minCargaM3 - m3, 3);
    agregar(
      'CARGA_INCOMPLETA',
      ITEMS_RECARGO.CARGA_INCOMPLETA,
      diferencia,
      'UF/M3',
      `${m3} m³ bajo el mínimo de ${e.minCargaM3} m³ en ${e.plantaCodigo}: se cobran ${diferencia} m³`,
    );
  }

  // ── 4. Bombeo: transformación de hormigón normal a bombeable ───────────
  if (p.grado === 'GB') {
    agregar('BOMBEO_HN_A_HB', ITEMS_RECARGO.BOMBEO, m3, 'UF/M3', 'Hormigón bombeable (GB)');
  }

  // ── 5. Aire incorporado 5% ─────────────────────────────────────────────
  if (p.extras.includes('AIR') && p.mpa !== null) {
    if ([25, 30, 35].includes(p.mpa)) {
      agregar(
        'AIRE_25_35',
        ITEMS_RECARGO.AIRE_25_35,
        m3,
        'UF/M3',
        `Aire incorporado 5% · G${p.mpa}`,
      );
    } else if ([40, 45, 50].includes(p.mpa)) {
      agregar(
        'AIRE_40_50',
        ITEMS_RECARGO.AIRE_40_50,
        m3,
        'UF/M3',
        `Aire incorporado 5% · G${p.mpa}`,
      );
    }
  }

  // ── 6. Diferencia de cono ──────────────────────────────────────────────
  if (p.grado === 'SH') {
    // Shotcrete: m³ × 7, sin importar el cono declarado.
    agregar(
      'DIF_CONO_SHOTCRETE',
      ITEMS_RECARGO.DIF_CONO_SHOTCRETE,
      m3 * FACTOR_CONO_SHOTCRETE,
      'UF/M3',
      `Diferencia de cono shotcrete: ${m3} m³ × ${FACTOR_CONO_SHOTCRETE}`,
    );
  } else if ((p.grado === 'GN' || p.grado === 'GB') && p.cono !== null && p.cono !== CONO_BASE) {
    const dif = Math.abs(p.cono - CONO_BASE);
    agregar(
      'DIF_CONO_HORMIGON',
      ITEMS_RECARGO.DIF_CONO_HORMIGON,
      dif * m3,
      'UF/M3',
      `Cono ${p.cono} vs base ${CONO_BASE}: ${dif} × ${m3} m³`,
    );
  }

  // ── 7. Fibra aporte Xtreme ─────────────────────────────────────────────
  const extraFibra = p.extras.find((x) => x in KG_FIBRA_POR_M3);
  if (extraFibra) {
    const kgPorM3 = KG_FIBRA_POR_M3[extraFibra]!;
    agregar(
      'FIBRA_APORTE_XTREME',
      ITEMS_RECARGO.FIBRA,
      kgPorM3 * m3,
      'UF/KG',
      `Fibra ${extraFibra}: ${kgPorM3} kg/m³ × ${m3} m³`,
    );
  }

  // ── 8. Resistencia temprana ────────────────────────────────────────────
  if (p.extras.includes('R3')) {
    agregar('RESISTENCIA_R3', ITEMS_RECARGO.R3, m3, 'UF/M3', 'Resistencia temprana R3');
  }
  if (p.extras.includes('R7')) {
    agregar('RESISTENCIA_R7', ITEMS_RECARGO.R7, m3, 'UF/M3', 'Resistencia temprana R7');
  }

  // ── 9. Nivel de confianza 95% ──────────────────────────────────────────
  if (p.pctConfianza === 95) {
    agregar('CONFIANZA_95', ITEMS_RECARGO.CONFIANZA_95, m3, 'UF/M3', 'Confianza 90% → 95%');
  }

  // ── 10. Aditivo impermeabilizante ──────────────────────────────────────
  if (p.extras.includes('IMP')) {
    agregar(
      'IMPERMEABILIZANTE',
      ITEMS_RECARGO.IMPERMEABILIZANTE,
      m3,
      'UF/M3',
      'Aditivo impermeabilizante shotcrete 2%',
    );
  }

  return out;
}

/** Suma en UF de una lista de recargos. */
export function totalRecargosUf(recargos: RecargoCalculado[]): number {
  return roundTo(
    recargos.reduce((s, r) => s + r.montoUf, 0),
    4,
  );
}
